import type { WorkerToTabMessage } from "@/app/shared/services/realtime/realtime-worker";
import { Channel } from "../types";
import { BaseChannel } from "./base-channel";

enum AnalysisNoteEvent {
  Changed = "ANALYSIS_NOTE.CHANGED",
}

interface AnalysisNoteEventPayload {
  /** Encoded id of the analysis whose notes & history thread changed. */
  analysis_id: string;
}

/**
 * Safety net for a worker that reports neither a connect nor a connect error, e.g. a
 * handshake the server accepts but never answers. Normally the worker reports the socket
 * state on its own (see BaseChannel.isRealtimeReachable), so the join does not sit out
 * this timeout before falling back to HTTP.
 */
const REALTIME_JOIN_TIMEOUT_MS = 5000;

/**
 * HTTP fallback interval, used whenever the socket is not (or no longer) up - local dev
 * usually runs without a realtime server at all. The listener refetches the thread, which
 * is exactly what a realtime event makes it do, so the interval is the whole fallback.
 */
const HTTP_POLL_INTERVAL_MS = 15000;

type AnalysisNoteChangedListener = () => void;

/**
 * Realtime channel for an analysis' notes & history thread ("chat"). Joins the room of the
 * analysis whose thread is open and notifies its listeners whenever anybody appends, edits,
 * deletes or reacts to an entry - or the system logs one - so the thread can refetch itself
 * while it stays open.
 *
 * The listeners are served no matter what the socket does: while it is unreachable a
 * periodic tick takes over, and a reconnect resyncs once, because changes made while the
 * socket was down were never delivered.
 */
export class AnalysisNoteChannel extends BaseChannel {
  readonly channelName = Channel.ANALYSIS_NOTE;

  private readonly changedListeners = new Set<AnalysisNoteChangedListener>();
  private joinedAnalysisId: string | null = null;
  private joinedRealtime = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  /** True once the first "connected" arrived; later ones indicate a reconnect. */
  private hasReceivedConnected = false;

  constructor() {
    super();

    this.port.addEventListener("message", (event: MessageEvent<WorkerToTabMessage>) => {
      const msg = event.data;

      if (msg.type === "connected") {
        void this.onSocketConnected();
      } else if (msg.type === "unavailable") {
        // The socket is down (and stays down until it reports a connect again): keep the
        // thread fresh over HTTP in the meantime.
        if (this.joinedAnalysisId) {
          this.startPolling();
        }
      } else if (msg.type === "event" && msg.eventName === AnalysisNoteEvent.Changed) {
        // The SharedWorker socket is shared across tabs; only react to events of the
        // analysis this tab actually joined.
        const payload = msg.payload as AnalysisNoteEventPayload | undefined;
        if (payload?.analysis_id && payload.analysis_id === this.joinedAnalysisId) {
          this.notifyListeners();
        }
      }
    });
  }

  public async join(analysisId: string): Promise<void> {
    if (this.joinedAnalysisId === analysisId) {
      return;
    }

    await this.leave();
    this.joinedAnalysisId = analysisId;

    // Non-admin users are rejected by the realtime server and local dev often has none at
    // all, so ask the worker for the socket state instead of waiting for a "connected" that
    // may never arrive, and tick over HTTP while it is down.
    const isReachable = await this.isRealtimeReachable(REALTIME_JOIN_TIMEOUT_MS);

    // A leave() - or a "connected" that already joined the room - may have run in between.
    if (this.joinedAnalysisId !== analysisId || this.joinedRealtime) {
      return;
    }

    if (!isReachable) {
      this.startPolling();
      return;
    }

    this.joinedRealtime = true;
    await this.joinChannel(analysisId);
    this.stopPolling();
  }

  public async leave(): Promise<void> {
    this.stopPolling();

    if (!this.joinedAnalysisId) {
      return;
    }

    const analysisId = this.joinedAnalysisId;
    this.joinedAnalysisId = null;

    if (this.joinedRealtime) {
      this.joinedRealtime = false;
      await this.leaveChannel(analysisId);
    }
  }

  /** Subscribe to thread-changed events. Returns an unsubscribe function. */
  public subscribe(listener: AnalysisNoteChangedListener): () => void {
    this.changedListeners.add(listener);
    return () => {
      this.changedListeners.delete(listener);
    };
  }

  public async destroy(): Promise<void> {
    await this.leave();
    this.changedListeners.clear();
    this.close();
  }

  private async onSocketConnected(): Promise<void> {
    const isReconnect = this.hasReceivedConnected;
    this.hasReceivedConnected = true;

    const analysisId = this.joinedAnalysisId;
    if (!analysisId) {
      return;
    }

    const wasPolling = this.isPolling;
    if (!this.joinedRealtime) {
      // The socket came up only after this thread had already fallen back to HTTP.
      this.joinedRealtime = true;
      await this.joinChannel(analysisId);
    }
    // A reconnect re-joins the room on its own (see resubscribeAll in the worker).
    this.stopPolling();

    // Nothing was delivered while the room was not joined, so resync once - but not for the
    // very first connect of a thread that has just loaded itself anyway.
    if (isReconnect || wasPolling) {
      this.notifyListeners();
    }
  }

  private get isPolling(): boolean {
    return this.pollTimer !== null;
  }

  private startPolling(): void {
    if (this.pollTimer !== null) {
      return;
    }

    this.pollTimer = setInterval(() => this.notifyListeners(), HTTP_POLL_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollTimer === null) {
      return;
    }

    clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  private notifyListeners(): void {
    // Iterate a copy: a listener may unsubscribe while being served.
    for (const listener of [...this.changedListeners]) {
      listener();
    }
  }
}
